import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Dropdown } from '@praetorian-chariot/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import { Modal } from '@/components/Modal';
import { useMy } from '@/hooks/useMy';
import {
  type PlextracReportInfo,
  usePushToPlexTrac,
} from '@/hooks/usePlextrac';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import type { DropdownOption } from '@/types/dropdown.types';
import { generatePathWithSearch } from '@/utils/url.util';

interface PushToPlexTracModalProps {
  isOpen: boolean;
  onClose: () => void;
  riskKey: string;
  riskName: string;
}

export function PushToPlexTracModal({
  isOpen,
  onClose,
  riskKey,
}: PushToPlexTracModalProps) {
  const { drawerState } = useGlobalState();
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState<DropdownOption | null>(
    null
  );
  const [search, setSearch] = useState('');

  const { me, isPraetorianUser } = useAuth();

  // Fetch connected reports from Configuration objects
  const {
    data: connectedReportsData,
    status: reportsStatus,
    error: reportsError,
  } = useMy(
    {
      resource: 'configuration',
      query: 'plextrac#report',
    },
    {
      enabled: isOpen,
    }
  );

  const { mutateAsync: pushToPlexTrac, status: pushToPlexTracStatus } =
    usePushToPlexTrac();

  // Transform Configuration objects to dropdown options
  const reportOptions = useMemo(() => {
    if (!connectedReportsData || connectedReportsData.length === 0) {
      return [];
    }

    return connectedReportsData
      .map((config: any) => {
        // Extract report ID from key like "#configuration#plextrac#report-936718458"
        const match = config.key?.match(/plextrac#report-(\d+)/);
        if (!match) return null;

        const reportId = match[1];

        // Value is now a ReportInfo struct with full report details
        const reportInfo = config.value as PlextracReportInfo;
        const reportName = reportInfo?.name
          ? `${reportId} - ${reportInfo.name}`
          : `${reportId}`;

        return {
          value: reportId,
          label: reportName,
        } as DropdownOption;
      })
      .filter((option): option is DropdownOption => option !== null);
  }, [connectedReportsData]);

  const filteredOptions = useMemo(
    () =>
      reportOptions.filter(option =>
        option.label.toLowerCase().includes(search.toLowerCase())
      ),
    [reportOptions, search]
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedReport(null);
      setSearch('');
    }
  }, [isOpen]);

  const handlePush = async () => {
    if (!selectedReport) {
      toast.error('Please select a report');
      return;
    }
    await pushToPlexTrac({ riskKey, reportID: Number(selectedReport?.value) });
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      title="Push Finding to PlexTrac Report"
      onClose={onClose}
      size="lg"
      footer={{
        className: 'w-full',
        text:
          pushToPlexTracStatus === 'pending'
            ? 'Pushing...'
            : 'Push to PlexTrac',
        onClick: handlePush,
        disabled:
          reportsStatus === 'pending' ||
          pushToPlexTracStatus === 'pending' ||
          !selectedReport,
        styleType: 'primary',
        'data-testid': 'plextrac-push-button',
      }}
    >
      <div className="space-y-4">
        <div data-testid="plextrac-report-dropdown">
          <Dropdown
            options={filteredOptions}
            selected={selectedReport ? [selectedReport] : []}
            onSelect={(option: DropdownOption) => setSelectedReport(option)}
            searchValue={search}
            onSearchChange={setSearch}
            className="w-full"
            listClassName="w-full"
            triggerClassName="w-full flex justify-between"
            searchPlaceholder="Search connected reports..."
            placeholder={
              reportsStatus === 'pending'
                ? 'Loading connected reports...'
                : reportOptions.length === 0
                  ? 'No connected reports'
                  : 'Select Report'
            }
          />
        </div>
        {reportOptions.length === 0 && reportsStatus === 'success' && (
          <div className="border-chariot-stroke-medium bg-chariot-status-info/10 text-chariot-text-primary flex gap-1 rounded border p-2 text-sm">
            <InformationCircleIcon className="text-chariot-status-info mr-1 inline size-5 shrink-0" />
            <div
              className="cursor-pointer"
              onClick={() => {
                onClose();
                drawerState.handleOpenVulnerabilityDrawer();
                navigate(
                  generatePathWithSearch({
                    pathname: AuthenticatedRoute.CUSTOMER_MANAGEMENT,
                    appendSearch: [
                      ['customerDrawerKey', me],
                      ['drawerOrder', '["customer"]'],
                    ],
                    hash: 'reporting',
                  })
                );
              }}
            >
              {`No connected reports found. Please connect a report in the ${isPraetorianUser ? 'Customer Management' : 'Tenant Management'} settings.`}
            </div>
          </div>
        )}
        {reportsStatus === 'error' && (
          <p className="border-chariot-stroke-medium bg-chariot-status-medium text-chariot-text-primary flex gap-1 rounded border p-2 text-sm">
            <ExclamationTriangleIcon className="text-chariot-text-primary mr-1 inline size-5 shrink-0" />
            <div
              className="cursor-pointer"
              onClick={() => {
                onClose();
                drawerState.handleOpenVulnerabilityDrawer();
                navigate(
                  generatePathWithSearch({
                    pathname: AuthenticatedRoute.SETTINGS,
                    appendSearch: [['tab', 'scanSettings']],
                  })
                );
              }}
            >
              {`Failed to load connected reports. Please check your PlexTrac configuration under Settings > Scan Settings > Reporting.`}
            </div>
          </p>
        )}
      </div>
    </Modal>
  );
}
